import { MouseEvent, forwardRef, useContext } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import {
  Button,
  ButtonProps,
  Tooltip,
  Typography,
  WithStyles,
  createStyles,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import { StepContext, useMultiStepDialogContext } from './context';

const styles = createStyles({
  root: {},
});

export type NextProps = WithStyles<typeof styles> &
  ButtonProps & {
    tooltip?: string;
  };

const Next = forwardRef<HTMLButtonElement, NextProps>((props, ref) => {
  const {
    classes,
    className,
    disabled: disabledProp,
    onClick,
    children = 'Next',
    tooltip,
    ...rest
  } = props;
  const { onNextStep } = useMultiStepDialogContext();

  const { forceReview, seenEverything } = useContext(StepContext);
  const disabled = (forceReview && !seenEverything) || disabledProp;

  const handleNextStep = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    onNextStep();
  };

  useHotkeys('meta+enter', () => onNextStep(), {
    enabled: !disabled,
    // We want to allow the user to go to next step even if they have
    // focus on an input field.
    enableOnFormTags: true,
    preventDefault: true,
  });

  const button = (
    <Button
      ref={ref}
      className={classNames(classes.root, className)}
      variant="contained"
      color="primary"
      onClick={handleNextStep}
      disabled={disabled}
      {...rest}
    >
      {children}
    </Button>
  );

  if (disabled) {
    if (tooltip) {
      return (
        <Tooltip
          enterDelay={500}
          title={
            <Typography variant="body2" style={{ padding: 2 }}>
              {tooltip}
            </Typography>
          }
        >
          <span>{button}</span>
        </Tooltip>
      );
    }
    return button;
  }

  return (
    <Tooltip
      enterDelay={500}
      title={
        <Typography variant="body2" style={{ padding: 2 }}>
          {tooltip ?? 'Proceed to next step'}
        </Typography>
      }
    >
      {button}
    </Tooltip>
  );
});
Next.displayName = 'Next';

export default withStyles(styles)(Next);
